import React from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import default_theme from "assets/themes/themes";
import {
  Typography,
  Box,
  Container,
  Button,
  Grid,
  CircularProgress,
} from "@mui/material";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import styles from "./CatalogDetailPage.module.css";
import { fetchCatalogDetailRequest } from "../../../redux/catalog/catalogSlice";

import SolutionDetailSidebar from "../../../components/SolutionDetails/SolutionDetailSidebar";
import SolutionDetailContent from "../../../components/SolutionDetails/SolutionDetailContent";
import SolutionDetailHeader from "../../../components/SolutionDetails/SolutionDetailHeader";
import { buildSections } from "../../../components/SolutionDetails/catalogDetailUtils";
import useCatalogDetail from "../../../components/SolutionDetails/hooks/useCatalogDetail";
import SolutionDetailActions from "../../../components/SolutionDetails/SolutionDetailActions";

const CatalogDetailPage = () => {
  const { entryId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const catalogState = useSelector((state) => state.catalog);
  const authState = useSelector((state) => state.auth);
  const entry = entryId ? catalogState.catalogDetails[entryId] : null;
  const loading = catalogState.loading;
  const isAuthenticated = authState?.isAuthenticated ?? false;

  const {
    favorites,
    activeTab,
    briefDescriptionRef,
    handleTabChange,
    toggleFavorite,
    handleBackClick,
  } = useCatalogDetail({
    entryId,
    dispatch,
    navigate,
    location,
    isAuthenticated,
  });
  
  // Extract custom back path/label if provided by the previous page
  // 1. Read query parameters from the URL
  const queryParams = new URLSearchParams(location.search);
  const urlBackPath = queryParams.get("backPath");
  const urlBackLabel = queryParams.get("backLabel");

  // 2. Prioritize the URL params, then hidden state, then the default
  const customBackPath = urlBackPath || location.state?.backPath;
  const customBackLabel = urlBackLabel || location.state?.backLabel || "Back to Catalogue";

  const customHandleBackClick = () => {
    if (customBackPath) {
      navigate(customBackPath);
    } else {
      handleBackClick(); 
    }
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 10 }}>
        <Typography>
          <Box display="inline-flex" alignItems="center">
            <CircularProgress size={16} thickness={4} color={default_theme.palette.primary.black} />
            <Box ml={1}>Loading...</Box>
          </Box>
        </Typography>
      </Container>
    );
  }

  if (!entry || !entry.combinedData) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h5" gutterBottom>
            Solution not found
          </Typography>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={customHandleBackClick}
            variant="outlined"
            sx={{
              color: default_theme.palette.other.figmaBlue,
              borderColor: default_theme.palette.other.figmaBlue,
              '&:hover': {
                borderColor: default_theme.palette.other.figmaBlue,
                backgroundColor: default_theme.palette.other.tealBackgroundSoft,
              },
            }}
          >
            {customBackLabel}
          </Button>
        </Box>
      </Container>
    );
  }

  const data = entry.combinedData;
  const sections = buildSections(data);
  const prioritySections = sections.filter(s => s.priority);
  const otherSections = sections.filter(s => !s.priority);

  return (
    <Box className={styles.pageWrapper}>
      <Container maxWidth="xl">
        <SolutionDetailActions
          entry={entry}
          isAuthenticated={isAuthenticated}
          favorites={favorites}
          toggleFavorite={toggleFavorite}
          handleBackClick={customHandleBackClick}
          backLabel={customBackLabel}
          styles={styles}
        />

        <SolutionDetailHeader
          data={data}
          styles={styles}
        />

        <Box className={styles.contentSection}>
          <Grid container spacing={4}>

            <Grid item xs={12} md={4}>
              <SolutionDetailSidebar
                data={data}
                isAuthenticated={isAuthenticated}
                styles={styles}
              />
            </Grid>

            <Grid item xs={12} md={8}>
              <SolutionDetailContent
                prioritySections={prioritySections}
                otherSections={otherSections}
                activeTab={activeTab}
                handleTabChange={handleTabChange}
                briefDescriptionRef={briefDescriptionRef}
                styles={styles}
              />
            </Grid>

          </Grid>
        </Box>
      </Container>
    </Box>
  );
};

export default CatalogDetailPage;